import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsoLanguageCode } from '../../../../utils/hooks/useIsoLanguageCode';
import { getConsultationJotFormUrl } from '../../../../policyholders/resilience/utils/getConsultationJotFormUrl';
import styles from './MdrConsultationModal.module.css';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';

interface MdrConsultationModalProps {
  readonly onClose: () => void;
  readonly onSubmissionComplete: () => void;
}

export default function MdrConsultationModal({
  onClose,
  onSubmissionComplete,
}: MdrConsultationModalProps) {
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const isoLanguageCode = useIsoLanguageCode();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  const formUrl = getConsultationJotFormUrl(isoLanguageCode);
  const formOrigin = useMemo(() => new URL(formUrl).origin, [formUrl]);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    modalRef.current?.focus();
    return () => {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const trapTab = (event: KeyboardEvent) => {
      const modal = modalRef.current;
      if (!modal) return;

      const focusable = Array.from(
        modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === modal)) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab') trapTab(event);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Exact origin match — a substring check would also accept hostile
      // origins such as https://jotform.attacker.com.
      if (event.origin !== formOrigin) return;
      if (typeof event.data !== 'object' || !event.data) return;
      if (event.data.action === 'submission-completed') {
        onSubmissionComplete();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSubmissionComplete, formOrigin]);

  const handleOverlayClick = useCallback(() => onClose(), [onClose]);
  const handleModalClick = useCallback(
    (event: React.MouseEvent) => event.stopPropagation(),
    []
  );

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Request a Consultation"
        tabIndex={-1}
        onClick={handleModalClick}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close consultation modal"
        >
          &times;
        </button>
        {isIframeLoading && (
          <div className={styles.spinner}>
            <div className={styles.spinnerCircle} />
          </div>
        )}
        <iframe
          className={styles.iframe}
          src={formUrl}
          title="Request a Consultation"
          onLoad={() => setIsIframeLoading(false)}
        />
      </div>
    </div>,
    document.body
  );
}
